import {
  Badge,
  type BadgeProps,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

const MAX_TAG_LENGTH = 25;

/**
 * The design system's `BadgeProps` is a union: removable badges require `onRemove`,
 * plain ones forbid it. This badge is always plain, so it takes that branch — `Omit`
 * over the whole union would drop the discriminant and satisfy neither.
 */
type PlainBadgeProps = Extract<BadgeProps, { onRemove?: never }>;

export interface TruncatedBadgeProps extends Omit<PlainBadgeProps, 'children'> {
  tag: string;
}

export const TruncatedBadge = ({
  tag,
  variant = 'secondary',
  roundness = 'full',
  ...props
}: TruncatedBadgeProps) => {
  const isTruncated = tag.length > MAX_TAG_LENGTH;
  const displayText = isTruncated ? `${tag.slice(0, MAX_TAG_LENGTH)}…` : tag;

  const badge = (
    <Badge variant={variant} roundness={roundness} {...props}>
      {displayText}
    </Badge>
  );

  if (isTruncated) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>{tag}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
};
